import { MatDateFormats } from '@angular/material/core';
import { DateTime, Info, Settings } from 'luxon';
import { TAppDateInput } from './date.types';

export const APP_LOCALE = 'ru-RU';

export const APP_DATE_FORMATS = {
    UI_DATE: 'd MMMM yyyy',
    UI_DATE_CURRENT_YEAR: 'd MMMM',
    UI_DATE_TIME: "d MMMM yyyy 'в' HH:mm",
    UI_DATE_TIME_CURRENT_YEAR: "d MMMM 'в' HH:mm",
    INPUT_DATE: 'dd.MM.yyyy',
    TIME: 'HH:mm',
    API_DATE: 'yyyy-MM-dd',
    DATE_KEY: 'yyyyMMdd',
    MONTH_HEADER: 'LLLL yyyy',
    WEEKDAY: 'cccc',
} as const;

export const APP_MATERIAL_DATE_FORMATS: MatDateFormats = {
    parse: {
        dateInput: [APP_DATE_FORMATS.INPUT_DATE, APP_DATE_FORMATS.API_DATE],
        timeInput: APP_DATE_FORMATS.TIME,
    },
    display: {
        dateInput: APP_DATE_FORMATS.UI_DATE,
        timeInput: APP_DATE_FORMATS.TIME,
        monthYearLabel: APP_DATE_FORMATS.MONTH_HEADER,
        dateA11yLabel: 'DDDD',
        monthYearA11yLabel: APP_DATE_FORMATS.MONTH_HEADER,
        timeOptionLabel: APP_DATE_FORMATS.TIME,
    },
};

export function configureAppDateLocale(): void {
    Settings.defaultLocale = APP_LOCALE;
}

export function toDateTime(value: TAppDateInput): DateTime {
    if (DateTime.isDateTime(value)) {
        return value.setLocale(APP_LOCALE);
    }

    if (value instanceof Date) {
        return DateTime.fromJSDate(value).setLocale(APP_LOCALE);
    }

    return DateTime.fromMillis(value).setLocale(APP_LOCALE);
}

export function toDateTimeOrNull(
    value: TAppDateInput | null | undefined,
): DateTime | null {
    if (value === null || value === undefined) return null;

    const date = toDateTime(value);
    return date.isValid ? date : null;
}

export function parseDateTime(
    value: string,
    format: string,
): DateTime | null {
    const date = DateTime.fromFormat(value, format, {
        locale: APP_LOCALE,
        zone: 'local',
    });

    return date.isValid ? date : null;
}

export function formatDate(
    value: TAppDateInput | null | undefined,
    format: string = APP_DATE_FORMATS.UI_DATE,
): string {
    const date = toDateTimeOrNull(value);
    return date ? date.toFormat(format) : '';
}

export function formatUiDate(
    value: TAppDateInput | null | undefined,
): string {
    const date = toDateTimeOrNull(value);
    if (!date) return '';

    const format =
        date.year === DateTime.local().year
            ? APP_DATE_FORMATS.UI_DATE_CURRENT_YEAR
            : APP_DATE_FORMATS.UI_DATE;
    return date.toFormat(format);
}

export function formatUiDateTime(
    value: TAppDateInput | null | undefined,
): string {
    const date = toDateTimeOrNull(value);
    if (!date) return '';

    const format =
        date.year === DateTime.local().year
            ? APP_DATE_FORMATS.UI_DATE_TIME_CURRENT_YEAR
            : APP_DATE_FORMATS.UI_DATE_TIME;
    return date.toFormat(format);
}

export function formatInputDate(
    value: TAppDateInput | null | undefined,
): string {
    return formatDate(value, APP_DATE_FORMATS.INPUT_DATE);
}

export function formatTime(
    value: TAppDateInput | null | undefined,
): string {
    return formatDate(value, APP_DATE_FORMATS.TIME);
}

export function formatApiDate(
    value: TAppDateInput | null | undefined,
): string {
    return formatDate(value, APP_DATE_FORMATS.API_DATE);
}

export function getDateKey(
    value: TAppDateInput | null | undefined,
): number {
    const formatted = formatDate(value, APP_DATE_FORMATS.DATE_KEY);
    return formatted ? Number(formatted) : 0;
}

export function isSameDay(
    left: TAppDateInput | null | undefined,
    right: TAppDateInput | null | undefined,
): boolean {
    const leftDate = toDateTimeOrNull(left);
    const rightDate = toDateTimeOrNull(right);
    return Boolean(leftDate && rightDate && leftDate.hasSame(rightDate, 'day'));
}

export function uniqueDateTimesByDay(values: DateTime[]): DateTime[] {
    const uniqueDates = new Map<string, DateTime>();

    values.forEach((value) => {
        const key = value.toISODate();
        if (value.isValid && key && !uniqueDates.has(key)) {
            uniqueDates.set(key, value);
        }
    });

    return [...uniqueDates.values()];
}

export function formatMonthHeader(value: TAppDateInput): string {
    const month = formatDate(value, APP_DATE_FORMATS.MONTH_HEADER);
    return month ? month.charAt(0).toUpperCase() + month.slice(1) : '';
}

export function getShortWeekdays(): string[] {
    return Info.weekdays('short', { locale: APP_LOCALE }).map((weekday) =>
        weekday.replace('.', '').replace(/^./, (letter) => letter.toUpperCase()),
    );
}
